import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectorRef,
  inject,
  DestroyRef,
  ViewChild,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  HostListener,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import {
  buildOpportunityKpiHint,
  formatOpportunityKpi,
  resolveOpportunityKpiAmounts,
} from '../../opportunity-kpi.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Company, CrmTag, Partner } from '@app/shared';
import { GlobalValuesService } from '@app/core';
import { getSafeImageUrl, OpportunityService, PartnerService } from '@app/core';
import { finalize, take } from 'rxjs';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ModifyPartnerDrawerComponent } from 'src/app/features/maestros/drawers/modify-partner-drawer/modify-partner-drawer.component';
import { QuillEditorComponent } from 'ngx-quill';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ProjectService } from '@app/core';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { resolveSaleOrderNavigationReference } from '../../quotation-name-display.util';

@Component({
  selector: 'app-opportunity-content',
  standalone: false,
  templateUrl: './opportunity-content.component.html',
  styleUrl: './opportunity-content.component.less',
})
export class OpportunityContentComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @Input() opportunity: any = null;
  @Input() editPermissions: boolean = false;
  @Input() isEditModeEnabled: boolean = false;
  @Input() activeTabIndex: number = 0;
  @Input() documents: any[] = [];
  @Input() budgets: any[] = [];
  @Input() activities: any[] = [];
  @Input() editModeAction: 'none' | 'save' | 'cancel' = 'none';
  @Output() updateEvent = new EventEmitter<any>();
  @Output() editModeChange = new EventEmitter<boolean>();
  @Output() fieldUpdate = new EventEmitter<{
    path: string;
    value: any;
    previousValue?: any;
    error?: boolean;
    errorMessage?: string;
  }>();
  @Output() budgetClick = new EventEmitter<any>();
  @Output() budgetDeleted = new EventEmitter<number>();
  @Output() projectDeleted = new EventEmitter<number>();
  @Output() navigateToProjectRequest = new EventEmitter<number>();

  @ViewChild('deleteBudgetConfirmationModal')
  deleteBudgetConfirmationModal!: ConfirmationModalComponent;
  @ViewChild('deleteProjectConfirmationModal')
  deleteProjectConfirmationModal!: ConfirmationModalComponent;
  @ViewChild('quillEditor', { static: false })
  quillEditor?: QuillEditorComponent;
  @ViewChild('linkModalTemplate', { static: false })
  linkModalTemplate!: TemplateRef<any>;
  @ViewChild('linkInput', { static: false })
  linkInput?: ElementRef<HTMLInputElement>;
  private pendingDeleteBudgetId: number | null = null;
  private pendingDeleteProjectId: number | null = null;
  linkModalUrl: string = '';
  private linkModalQuill: any = null;
  private linkModalRange: { index: number; length: number } | null = null;
  private linkModalRef: any = null;

  // Datos de referencia
  stages: any[] = [];
  companies: Company[] = [];
  companiesList: Partner[] = []; // Lista de empresas cliente (isCompany = true)
  companiesListLoaded = false; // Evita mostrar '-' antes del valor real en Empresa Cliente
  contactList: Partner[] = []; // Lista de contactos filtrados por empresa cliente
  contactLoading: boolean = false; // Estado de carga de contactos
  costCenters: any[] = [];
  currencies: any[] = [];
  tags: CrmTag[] = [];
  projects: any[] = [];
  filteredUsersForTeam: any[] = []; // Lista de usuarios/comerciales filtrados por equipo

  // Estado de edición
  currentEditingField: string | null = null;
  backupFieldValues: { [key: string]: any } = {};
  tempFieldValues: { [key: string]: any } = {};
  afterViewInit: boolean = false;
  fieldErrors: { [key: string]: string } = {}; // Errores por campo
  isInsertingImage: boolean = false; // Bandera para evitar emisiones durante inserción de imágenes
  isAddingLink: boolean = false; // Bandera para evitar que se cierre el modo edición al abrir el modal de link
  private pendingImageContent: string | null = null; // Contenido pendiente con imagen para emitir después
  private lastEmittedContent: string | null = null; // Último contenido emitido para evitar duplicados

  // Configuración de Quill para texto enriquecido
  quillConfig = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }], // custom button values
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
        [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
        [{ direction: 'rtl' }], // text direction
        [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        [{ font: [] }],
        [{ align: [] }],
        ['clean'], // remove formatting button
        ['link'],
      ],
      handlers: {
        link: (value: boolean) => {
          const quill = this.quillEditor?.quillEditor;
          if (!quill) return;

          const range = quill.getSelection(true);

          if (value) {
            // Agregar o editar link
            if (range && range.length > 0) {
              const formats = quill.getFormat(range);
              const currentLink = formats['link'] as string | undefined;
              this.showLinkModal(currentLink || '', quill, {
                index: range.index,
                length: range.length,
              });
            } else {
              // No hay texto seleccionado, informar al usuario
              this.modalService.warning({
                nzTitle: 'Seleccione texto',
                nzContent:
                  'Por favor, seleccione el texto al que desea agregar el enlace.',
                nzCentered: true,
                nzWidth: 400,
              });
            }
          } else {
            // Remover link (cuando se hace clic en el botón de link que ya está activo)
            if (range && range.length > 0) {
              quill.format('link', false);
            }
          }
        },
      },
    },
  };

  private quillTextChangeListener: any = null; // Listener para cambios de texto en Quill
  private modalService = inject(NzModalService);
  private viewContainerRef = inject(ViewContainerRef);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private projectService = inject(ProjectService);

  // Propiedades computadas (en lugar de funciones en la vista)
  opportunityName: string = '-';
  opportunityNameForInput: string = '';
  currentOpportunityId: number = 0;
  opportunityUsers: any[] = [];
  stagesForTeam: any[] = [];
  totalRevenue: string = '$0,00 USD';
  totalExpenses: string = '$0,00 USD';
  expectedRevenue: string = '$0,00 USD';
  kpiCurrencyHint: string = '';
  probability: string = '-';
  probabilityForInput: number = 0;
  currentStageName: string = 'Nuevo';
  opportunityTypeDisplay: string = '-';
  opportunityTypeForSelect: string = '';
  contactName: string = '-';
  commercialName: string = '-';
  companyName: string = '-';
  costCenterName: string = '-';
  intermediaryCompanyName: string = '-';
  salesTeamName: string = '-';
  priorityName: string = '-';
  priorityStars: boolean[] = [false, false, false, false];
  opportunityTags: CrmTag[] = [];
  tagIds: number[] = [];
  formattedExpectedClosingDate: string = '-';
  dateForPicker: Date | null = null;

  opportunityStages = [
    { name: 'Nuevo', order: 1 },
    { name: 'Evaluación', order: 2 },
    { name: 'Presupuesto', order: 3 },
    { name: 'Pausada', order: 4 },
    { name: 'Adjudicada', order: 5 },
    { name: 'Cancelada', order: 6 },
  ];

  ModifyPartnerDrawerComponent = ModifyPartnerDrawerComponent;

  protected readonly getSafeImageUrl = getSafeImageUrl;
  private destroyRef = inject(DestroyRef);
  protected readonly globalValuesService = inject(GlobalValuesService);
  private opportunityService = inject(OpportunityService);
  private partnerService = inject(PartnerService);

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {}

  /**
   * Inicializa el componente cargando los datos de referencia
   * y actualizando las propiedades computadas
   */
  ngOnInit(): void {
    this.loadReferenceData();
    this.updateComputedProperties();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cuando se hace rollback desde saveAllPendingChanges (editModeAction = 'cancel')
    if (changes['editModeAction']) {
      const previousAction = changes['editModeAction'].previousValue;
      const currentAction = changes['editModeAction'].currentValue;

      if (currentAction === 'cancel' && previousAction !== 'cancel') {
        // Verificar si opportunity tiene campos con error marcados (rollback después de error)
        const fieldsWithErrors = (this.opportunity as any)
          ?.__fieldsWithErrors as string[] | undefined;

        if (fieldsWithErrors && fieldsWithErrors.length > 0) {
          // Es un rollback después de un error al guardar - marcar errores
          console.log(
            '🔄 Rollback después de error - marcando campos con error:',
            fieldsWithErrors
          );

          fieldsWithErrors.forEach((fieldPath) => {
            this.fieldErrors[fieldPath] = 'Error al guardar';
          });

          this.cdr.detectChanges();
        } else {
          // Es un cancelar normal (botón Cancelar) - limpiar errores
          console.log('🔄 Cancelar normal - limpiando errores');
          this.fieldErrors = {};
          this.cdr.detectChanges();
        }
      }
    }

    // También detectar cuando opportunity cambia después de un rollback
    if (
      changes['opportunity'] &&
      !changes['opportunity'].firstChange &&
      this.editModeAction === 'cancel'
    ) {
      const fieldsWithErrors = (this.opportunity as any)?.__fieldsWithErrors as
        | string[]
        | undefined;

      if (fieldsWithErrors && fieldsWithErrors.length > 0) {
        // Marcar cada campo como con error
        fieldsWithErrors.forEach((fieldPath) => {
          this.fieldErrors[fieldPath] = 'Error al guardar';
        });

        this.cdr.detectChanges();
      }
    }

    if (changes['opportunity']) {
      console.log('📊 [opportunity-content] Opportunity cambió:', changes['opportunity'].currentValue);
      const previousOpportunity = changes['opportunity'].previousValue;
      const currentOpportunity = changes['opportunity'].currentValue;
      
      // Si hay un campo en edición y NO es el primer cambio, verificar si se revirtió (error)
      if (
        !changes['opportunity'].firstChange &&
        this.currentEditingField &&
        previousOpportunity &&
        currentOpportunity
      ) {
        const fieldPath = this.currentEditingField;
        const previousValue = previousOpportunity[fieldPath];
        const currentValue = currentOpportunity[fieldPath];
        const backupValue = this.backupFieldValues[fieldPath];

        // Si el valor actual coincide con el backup pero es diferente del valor anterior,
        // significa que hubo un error y se revirtió
        if (
          backupValue !== undefined &&
          currentValue !== undefined &&
          currentValue === backupValue &&
          previousValue !== backupValue &&
          previousValue !== currentValue
        ) {
          // Marcar el campo como con error
          this.fieldErrors[fieldPath] = 'Error al guardar';
          this.cdr.detectChanges();
        }
      }

      this.updateComputedProperties();
    }
    // CEC-565: las cotizaciones son las que alimentan el detalle de conversion del KPI, y
    // pueden llegar despues que la oportunidad. Antes se escuchaba a `activities`, que ya no
    // interviene en ningun calculo de esta pantalla.
    if (changes['budgets'] && !changes['budgets'].firstChange) {
      this.updateComputedProperties();
    }
    if (changes['isEditModeEnabled']) {
      // IMPORTANTE:
      // - Si se desactiva el modo edición por "cancel", debemos hacer rollback del campo actual.
      // - Si se desactiva porque se guardó (o por cualquier otro motivo), NO debemos emitir el valor anterior,
      //   ya que eso puede provocar una segunda petición con el texto viejo (especialmente con Quill si se pulsa Guardar
      //   mientras el editor sigue abierto).
      if (!this.isEditModeEnabled && this.currentEditingField) {
        if (this.editModeAction === 'cancel') {
          this.cancelFieldEdit(this.currentEditingField);
        } else {
          this.stopEditingField(this.currentEditingField);
        }
      }
      // Si se desactiva el modo de edición (cancelar), limpiar todos los errores
      if (!this.isEditModeEnabled) {
        this.fieldErrors = {};
        this.cdr.detectChanges();
      }
    }
  }

  ngAfterViewInit(): void {
    this.afterViewInit = true;

    // Configurar listener de Quill después de que la vista se inicialice
    setTimeout(() => {
      this.setupQuillTextChangeListener();
    }, 500);
  }

  private setupQuillTextChangeListener(): void {
    // Este método se llamará cuando el editor Quill esté disponible
    // Se configurará en onQuillEditorCreated si es necesario
  }

  showLinkModal(
    currentUrl: string,
    quill: any,
    range: { index: number; length: number }
  ): void {
    this.isAddingLink = true;
    this.linkModalUrl = currentUrl || '';
    this.linkModalQuill = quill;
    this.linkModalRange = range;
    this.cdr.detectChanges();

    this.linkModalRef = this.modalService.create({
      nzTitle: currentUrl ? 'Editar enlace' : 'Agregar enlace',
      nzContent: this.linkModalTemplate,
      nzFooter: null,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { component: this },
      nzBodyStyle: { padding: '24px' },
      nzCentered: true,
      nzWidth: 500,
      nzClosable: true,
      nzMaskClosable: false,
    });

    this.linkModalRef.afterClose.subscribe(() => {
      setTimeout(() => {
        this.isAddingLink = false;
      }, 150);
    });

    setTimeout(() => {
      if (this.linkInput?.nativeElement) {
        this.linkInput.nativeElement.focus();
        this.linkInput.nativeElement.select();
      }
    }, 200);
  }

  closeLinkModal(): void {
    this.linkModalRef?.close();
    this.linkModalQuill = null;
    this.linkModalRange = null;
    this.linkModalUrl = '';
    this.isAddingLink = false;
  }

  /** Quita el formato de enlace del texto seleccionado y lo deja como texto normal. */
  removeLinkFromSelection(): void {
    if (!this.linkModalQuill || !this.linkModalRange) {
      this.closeLinkModal();
      return;
    }
    this.linkModalRef?.close();
    setTimeout(() => {
      if (!this.linkModalQuill || !this.linkModalRange) {
        this.linkModalQuill = null;
        this.linkModalRange = null;
        this.linkModalUrl = '';
        this.isAddingLink = false;
        return;
      }
      this.linkModalQuill.focus();
      this.linkModalQuill.setSelection(
        this.linkModalRange.index,
        this.linkModalRange.length
      );
      this.linkModalQuill.format('link', false);
      if (this.opportunity && this.linkModalQuill.root) {
        this.opportunity['description'] = this.linkModalQuill.root.innerHTML;
        this.lastEmittedContent = this.opportunity['description'];
        this.cdr.detectChanges();
      }
      this.linkModalQuill = null;
      this.linkModalRange = null;
      this.linkModalUrl = '';
      this.isAddingLink = false;
    }, 100);
  }

  onLinkModalEnter(): void {
    this.onLinkModalConfirm();
  }

  private normalizeLinkUrl(url: string): string {
    const u = url.trim();
    if (!u) return u;
    if (/^https?:\/\//i.test(u)) return u;
    return 'https://' + u;
  }

  onLinkModalConfirm(): void {
    const url = (this.linkModalUrl || '').trim();
    if (!url || !this.linkModalQuill || !this.linkModalRange) {
      this.closeLinkModal();
      return;
    }
    const normalized = this.normalizeLinkUrl(url);
    this.linkModalRef?.close();
    setTimeout(() => {
      if (!this.linkModalQuill || !this.linkModalRange) {
        this.linkModalQuill = null;
        this.linkModalRange = null;
        this.linkModalUrl = '';
        this.isAddingLink = false;
        return;
      }
      this.linkModalQuill.focus();
      this.linkModalQuill.setSelection(
        this.linkModalRange.index,
        this.linkModalRange.length
      );
      this.linkModalQuill.format('link', normalized);
      if (this.opportunity && this.linkModalQuill.root) {
        this.opportunity['description'] = this.linkModalQuill.root.innerHTML;
        this.lastEmittedContent = this.opportunity['description'];
        this.cdr.detectChanges();
      }
      this.linkModalQuill = null;
      this.linkModalRange = null;
      this.linkModalUrl = '';
      this.isAddingLink = false;
    }, 100);
  }

  sanitizeHtml(html?: string | null): any {
    if (!html) return '';
    try {
      return this.sanitizer.bypassSecurityTrustHtml(html);
    } catch (e) {
      console.error('Error sanitizing HTML:', e);
      return '';
    }
  }

  onQuillEditorCreated(editor: any): void {
    if (editor && editor.quillEditor) {
      const quill = editor.quillEditor;

      // Remover listener anterior si existe
      if (this.quillTextChangeListener) {
        quill.off('text-change', this.quillTextChangeListener);
      }

      // Crear nuevo listener que ignore cambios durante inserción de imágenes
      this.quillTextChangeListener = (
        delta: any,
        oldDelta: any,
        source: string
      ) => {
        // Si se está insertando una imagen, ignorar este evento
        if (this.isInsertingImage) {
          return;
        }

        // Solo procesar cambios de usuario, no cambios programáticos durante inserción de imágenes
        if (source === 'user' && !this.isInsertingImage) {
          // El ngModelChange se encargará de actualizar el valor
          // Este listener solo es para debugging o lógica adicional si es necesario
        }
      };

      // Agregar listener
      quill.on('text-change', this.quillTextChangeListener);
    }
  }

  loadReferenceData(): void {
    this.globalValuesService.stages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((stages: { id: number; name: string }[]) => {
        this.stages = stages || [];
        this.updateComputedProperties();
      });

    this.globalValuesService.companies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companies: Company[]) => {
        this.companies = companies || [];
        this.updateComputedProperties();
      });

    // Cargar empresas cliente (isCompany = true)
    this.partnerService
      .getCompanies()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companies: Partner[]) => {
        console.log('📊 [loadReferenceData] companiesList cargada:', companies?.length, 'empresas');
        this.companiesList = companies || [];
        this.companiesListLoaded = true;
        this.updateComputedProperties();
      });

    this.globalValuesService.costCenters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((costCenters: { id: number; name: string }[]) => {
        this.costCenters = costCenters || [];
        this.updateComputedProperties();
      });

    this.globalValuesService.currencies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currencies: any[]) => {
        this.currencies = currencies || [];
        this.updateComputedProperties();
      });

    this.globalValuesService.crmTags$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tags: CrmTag[]) => {
        this.tags = tags || [];
        this.updateComputedProperties();
      });

    // CEC-565: updateComputedProperties LEE salesTeams$ y users$ -- de ahi salen el comercial
    // de la oportunidad y la lista filtrada por equipo -- pero no se escuchaba ninguna de las
    // dos. Mientras existio la rama de `activities`, su llegada tardia recalculaba por
    // accidente y lo tapaba; al retirarla se vio que el comercial desaparecia si el catalogo
    // cargaba despues. Se escucha lo que se lee, igual que las demas tiendas de arriba.
    this.globalValuesService.salesTeams$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateComputedProperties());

    this.globalValuesService.users$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateComputedProperties());

    // La empresa cliente se resuelve contra `partnerlist`, que es un array plano y no se puede
    // escuchar. isPartnerReady$ es la señal que el servicio publica para eso, y es la que ya
    // usan info-card, avatar-select y los maestros de partner y usuario.
    this.globalValuesService.isPartnerReady$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateComputedProperties());
  }

  updateComputedProperties(): void {
    console.log('📊 [opportunity-content] updateComputedProperties llamado');
    console.log('📊 [opportunity-content] opportunity:', this.opportunity);
    
    if (!this.opportunity) {
      console.warn('⚠️ [opportunity-content] No hay opportunity');
      return;
    }

    // Opportunity name
    if (this.opportunity?.oportunidad && this.opportunity.oportunidad !== '-') {
      this.opportunityName = this.opportunity.oportunidad;
      this.opportunityNameForInput = this.opportunity.oportunidad;
    } else if (
      this.opportunity?.originalOpportunityName &&
      this.opportunity.originalOpportunityName !== '-'
    ) {
      this.opportunityName = this.opportunity.originalOpportunityName;
      this.opportunityNameForInput = this.opportunity.originalOpportunityName;
    } else {
      if (this.opportunity?.name && this.opportunity.name !== '-') {
        this.opportunityName = this.opportunity.name;
        this.opportunityNameForInput = this.opportunity.name;
      } else {
        this.opportunityName = '-';
        this.opportunityNameForInput = '';
      }
    }

    // Current opportunity ID
    this.currentOpportunityId =
      this.opportunity?.id || this.opportunity?.originalId || 0;

    // Opportunity users
    this.opportunityUsers = this.getOpportunityUsersValue();

    // Stages for team
    this.stagesForTeam = this.getStagesForTeamValue();

    // CEC-565: los tres KPI vienen del backend ya convertidos a la moneda de referencia y se
    // pintan con su divisa. El margen es siempre Importe menos Gasto, nunca lo guardado en
    // expectedRevenue.
    //
    // Se retira la rama que sumaba importes de las actividades: el modelo Activity no tiene
    // campos de importe, asi que nunca se ejecutaba y solo duplicaba el calculo.
    const kpi = resolveOpportunityKpiAmounts(this.opportunity);

    this.totalRevenue = formatOpportunityKpi(kpi.revenue);
    this.totalExpenses = formatOpportunityKpi(kpi.expenses);
    this.expectedRevenue = formatOpportunityKpi(kpi.margin);
    this.kpiCurrencyHint = buildOpportunityKpiHint(this.budgets, this.currencies);

    // Probability (fallback a probabilidad de la etapa si no viene en la oportunidad)
    let probability = this.opportunity?.probability;
    if (probability === null || probability === undefined) {
      const stageProb = this.getProbabilityFromStageId(
        this.opportunity?.stageId
      );
      if (stageProb !== null && stageProb !== undefined) {
        probability = stageProb;
      }
    }
    if (probability === null || probability === undefined) {
      this.probability = '-';
      this.probabilityForInput = 0;
    } else {
      this.probability = `${probability}%`;
      this.probabilityForInput = probability;
    }

    // Current stage name
    this.currentStageName =
      this.getStageName(this.opportunity?.stageId) || 'Nuevo';

    // Opportunity type
    const type = this.opportunity?.opportunityType;
    if (type === 'project' || type === 'Project') {
      this.opportunityTypeDisplay = 'Proyecto';
      this.opportunityTypeForSelect = 'Proyecto';
    } else if (type === 'punctualservice' || type === 'PunctualService') {
      this.opportunityTypeDisplay = 'Servicio Puntual';
      this.opportunityTypeForSelect = 'Servicio Puntual';
    } else {
      this.opportunityTypeDisplay = type || '-';
      this.opportunityTypeForSelect = type || '';
    }

    // Contact name
    this.contactName = this.getContactNameValue();
    console.log('📊 contactName:', this.contactName);

    // Commercial name
    console.log('📊 opportunity.comercialUser:', this.opportunity?.comercialUser);
    console.log('📊 opportunity.userId:', this.opportunity?.userId);
    console.log('📊 opportunity.user:', (this.opportunity as any)?.user);
    this.commercialName = this.getCommercialNameValue();
    console.log('📊 commercialName calculado:', this.commercialName);

    // Company name
    this.companyName = this.getCompanyNameValue(this.opportunity?.companyId);
    console.log('📊 companyName calculado:', this.companyName);

    // Cost center name
    this.costCenterName = this.getCostCenterNameValue(
      this.opportunity?.costCenterId
    );

    // Intermediary company name
    this.intermediaryCompanyName = this.getIntermediaryCompanyNameValue(
      this.opportunity?.intermediaryCompanyId
    );

    // Sales team name
    console.log('📊 opportunity.teamId:', this.opportunity?.teamId);
    console.log('📊 opportunity.team:', (this.opportunity as any)?.team);
    this.salesTeamName = this.getSalesTeamNameValue();
    console.log('📊 salesTeamName calculado:', this.salesTeamName);

    // Filtrar usuarios/comerciales por equipo
    this.filterUsersByTeam(this.opportunity?.teamId);

    // Priority
    this.priorityName = this.getPriorityNameValue();
    this.priorityStars = this.getPriorityStarsValue();

    // Tags
    this.opportunityTags = this.getOpportunityTagsValue();
    this.tagIds = this.getTagIdsValue();

    // Expected closing date
    this.formattedExpectedClosingDate = this.formatDateValue(
      this.opportunity?.expectedClosingDate
    );
    this.dateForPicker = this.getDateForPickerValue(
      this.opportunity?.expectedClosingDate
    );

    // Empresa cliente = Partner id (companiesList), no companyId. Priorizar clientCompanyId y contacto parentId
    const oppAny = this.opportunity as any;
    const clientCompanyId = oppAny?.clientCompanyId;
    console.log('📊 Empresa cliente - clientCompanyId:', clientCompanyId);
    console.log('📊 Empresa cliente - opportunity.clientCompany:', this.opportunity?.clientCompany);
    console.log('📊 Empresa cliente - opportunity.partnerId:', this.opportunity?.partnerId);
    console.log('📊 Empresa cliente - companiesList.length:', this.companiesList?.length);
    
    if (clientCompanyId != null) {
      const company = this.companiesList.find((c: Partner) => c.id === clientCompanyId);
      console.log('📊 Empresa cliente - company encontrada por ID:', company);
      if (company) {
        this.opportunity.clientCompany = company.name;
        this.loadContactsByCompany(company.id);
      }
    } else if (this.opportunity?.partnerId) {
      // Derivar empresa cliente del parentId del contacto
      console.log('📊 Empresa cliente - Buscando por partnerId');
      const contactPartner =
        this.opportunity.contactPartner ||
        this.globalValuesService.partnerlist.find(
          (p: Partner) => p.id === this.opportunity.partnerId
        );
      console.log('📊 Empresa cliente - contactPartner:', contactPartner);
      console.log('📊 Empresa cliente - contactPartner.parentId:', contactPartner?.parentId);
      console.log('📊 Empresa cliente - globalValuesService.partnerlist.length:', this.globalValuesService.partnerlist?.length);
      
      if (contactPartner?.parentId) {
        const company = this.companiesList.find(
          (c: Partner) => c.id === contactPartner.parentId
        );
        console.log('📊 Empresa cliente - company encontrada por parentId:', company);
        if (company) {
          this.opportunity.clientCompany = company.name;
          oppAny.clientCompanyId = company.id;
          this.loadContactsByCompany(company.id);
        }
      } else if (this.opportunity.partnerId && !contactPartner?.parentId) {
        // Si no hay parentId, intentar cargarlo desde el API
        console.log('📊 Empresa cliente - Cargando partner completo desde API');
        this.partnerService.postAdvanced({
          filterGroups: [[
            { property: 'id', value: this.opportunity.partnerId.toString(), operator: 'Equals' }
          ]],
          startRow: 0,
          endRow: 1,
          sortDirection: 'asc',
          sortProperty: 'id'
        })
          .pipe(take(1))
          .subscribe((partners: Partner[]) => {
            console.log('📊 Empresa cliente - Partners del API:', partners);
            const fullPartner = partners?.[0];
            console.log('📊 Empresa cliente - fullPartner.parentId:', fullPartner?.parentId);
            
            if (fullPartner?.parentId) {
              const company = this.companiesList.find(
                (c: Partner) => c.id === fullPartner.parentId
              );
              console.log('📊 Empresa cliente - company encontrada:', company);
              if (company) {
                this.opportunity.clientCompany = company.name;
                oppAny.clientCompanyId = company.id;
                this.opportunity.contactPartner = fullPartner;
                this.loadContactsByCompany(company.id);
                this.cdr.detectChanges();
              }
            }
          });
      }
    }
    if (this.opportunity?.clientCompany && !clientCompanyId) {
      const companyId = this.getClientCompanyId(this.opportunity.clientCompany);
      if (companyId) this.loadContactsByCompany(companyId);
    }
    // Si el API trae company (nested): usarlo solo si está en companiesList (Partner), no Compañía CRM
    if (oppAny?.clientCompanyId == null && oppAny?.company?.name) {
      const byName = this.companiesList.find(
        (c: Partner) => c.name === oppAny.company.name
      );
      if (byName) {
        this.opportunity.clientCompany = byName.name;
        oppAny.clientCompanyId = byName.id;
        this.loadContactsByCompany(byName.id);
      }
    }

    this.cdr.detectChanges();
  }

  // Helper methods para valores computados
  private getContactNameValue(): string {
    if (this.opportunity?.contactPartner) {
      return (
        `${this.opportunity.contactPartner.name || ''} ${
          this.opportunity.contactPartner.surName || ''
        }`.trim() || '-'
      );
    }
    if (this.opportunity?.partnerId) {
      const partners = this.globalValuesService.partnerlist || [];
      const partner = partners.find(
        (p: any) => p.id === this.opportunity.partnerId
      );
      if (partner) {
        return `${partner.name || ''} ${partner.surName || ''}`.trim() || '-';
      }
    }
    return '-';
  }

  private getCommercialNameValue(): string {
    if (this.opportunity?.comercialUser) {
      return (
        `${this.opportunity.comercialUser.name || ''} ${
          this.opportunity.comercialUser.surName || ''
        }`.trim() || '-'
      );
    }
    if (this.opportunity?.userId) {
      const users = this.globalValuesService.users$.value || [];
      const user = users.find((u: any) => u.id === this.opportunity.userId);
      if (user?.partner) {
        return (
          `${user.partner.name || ''} ${user.partner.surName || ''}`.trim() ||
          '-'
        );
      }
    }
    return '-';
  }

  private getCompanyNameValue(companyId: number | null): string {
    if (companyId === null || companyId === undefined) return '-';
    const company = this.companies.find((c: Company) => c.id === companyId);
    return company?.name || '-';
  }

  private getCostCenterNameValue(costCenterId: number | null): string {
    if (costCenterId === null || costCenterId === undefined) return '-';
    const costCenter = this.costCenters.find(
      (cc: any) => cc.id === costCenterId
    );
    return costCenter?.name || '-';
  }

  private getIntermediaryCompanyNameValue(
    intermediaryCompanyId: number | null
  ): string {
    // Fallback: si la API devuelve intermediaryCompany como objeto { id, name }
    const nested = (this.opportunity as any)?.intermediaryCompany;
    // Solo usar nested si coincide con el ID actual (evita mostrar nombre viejo tras cambiar selección)
    if (nested?.name && nested?.id === intermediaryCompanyId)
      return nested.name;
    if (intermediaryCompanyId === null || intermediaryCompanyId === undefined)
      return '-';
    const company = this.companiesList.find(
      (c: Partner) => c.id === intermediaryCompanyId
    );
    return company?.name || '-';
  }

  getCurrencyName(currencyId: number | null): string {
    if (currencyId === null || currencyId === undefined) return '-';
    const currency = this.currencies.find((c: any) => c.id === currencyId);
    return currency?.isoCode || currency?.name || '-';
  }

  getPartnerInitials(partner: any): string {
    if (!partner) return '?';
    const name = partner.name || '';
    const surName = partner.surName || '';
    let initials = '';
    if (name.trim()) initials += name.trim().charAt(0).toUpperCase();
    if (surName.trim()) initials += surName.trim().charAt(0).toUpperCase();
    return initials || '?';
  }

  getUserInitialsFromUser(user: any): string {
    if (!user) return '?';
    const partner = user.partner || {};
    const name = partner.name || user.name || '';
    const surName = partner.surName || user.surName || '';
    let initials = '';
    if (name.trim()) initials += name.trim().charAt(0).toUpperCase();
    if (surName.trim()) initials += surName.trim().charAt(0).toUpperCase();
    return initials || '?';
  }

  private getSalesTeamNameValue(): string {
    // Primero intentar obtener el nombre del objeto anidado 'team'
    const nested = (this.opportunity as any)?.team?.name;
    if (nested && String(nested).trim()) return String(nested).trim();
    
    // Si no está en el objeto anidado, buscar en globalValuesService
    if (!this.opportunity?.teamId) return '-';
    const salesTeams = this.globalValuesService.salesTeams$.value || [];
    const team = salesTeams.find((t: any) => t.id === this.opportunity.teamId);
    return team?.name || '-';
  }

  private getPriorityNameValue(): string {
    const priority = this.opportunity?.priority;
    if (priority === null || priority === undefined) return '-';

    if (typeof priority === 'string') {
      const priorityMap: { [key: string]: string } = {
        Low: 'Baja',
        Medium: 'Media',
        High: 'Alta',
        VeryHigh: 'Muy Alta',
      };
      return priorityMap[priority] || priority;
    }
    return '-';
  }

  private getPriorityStarsValue(): boolean[] {
    const priority = this.opportunity?.priority;
    if (priority === 'VeryHigh') return [true, true, true, true];
    if (priority === 'High') return [true, true, true, false];
    if (priority === 'Medium') return [true, true, false, false];
    if (priority === 'Low') return [true, false, false, false];
    return [false, false, false, false];
  }

  private getOpportunityTagsValue(): CrmTag[] {
    if (!this.opportunity?.tags) return [];
    if (Array.isArray(this.opportunity.tags)) {
      if (this.opportunity.tags.length === 0) return [];
      if (typeof this.opportunity.tags[0] === 'object') {
        return this.opportunity.tags;
      } else {
        return this.tags.filter((tag: CrmTag) =>
          this.opportunity.tags.includes(tag.id)
        );
      }
    }
    return [];
  }

  private getTagIdsValue(): number[] {
    if (!this.opportunity?.tags) return [];
    if (Array.isArray(this.opportunity.tags)) {
      if (this.opportunity.tags.length === 0) return [];
      if (typeof this.opportunity.tags[0] === 'object') {
        return this.opportunity.tags
          .map((tag: any) => tag.id || tag)
          .filter(Boolean);
      } else {
        return this.opportunity.tags;
      }
    }
    return [];
  }

  private getOpportunityUsersValue(): any[] {
    const users: any[] = [];
    let commercialUser = null;
    if (this.opportunity?.comercialUser) {
      commercialUser = {
        id: this.opportunity.comercialUser.id,
        name: this.opportunity.comercialUser.name,
        surName: this.opportunity.comercialUser.surName,
        avatar: this.opportunity.comercialUser.avatar,
      };
    } else if (this.opportunity?.userId) {
      const userList = this.globalValuesService.users$.value || [];
      const user = userList.find((u: any) => u.id === this.opportunity.userId);
      if (user?.partner) {
        commercialUser = {
          id: user.partner.id,
          name: user.partner.name,
          surName: user.partner.surName,
          avatar: user.partner.avatar,
        };
      }
    }
    if (commercialUser) {
      users.push(commercialUser);
    }
    return users;
  }

  private getStagesForTeamValue(): any[] {
    const teamId = this.opportunity?.teamId;
    return this.getStagesForTeam(teamId);
  }

  private getStagesForTeam(teamId: number | null | undefined): any[] {
    const allStages = this.globalValuesService.stages$.value || [];
    let filteredStages: any[] = [];

    if (teamId) {
      filteredStages = allStages.filter((stage: any) =>
        this.stageBelongsToTeam(stage, teamId)
      );
    } else {
      filteredStages = allStages.filter((stage: any) => this.stageBelongsToTeam(stage, teamId));
    }

    if (filteredStages.length === 0) {
      filteredStages = allStages;
    }

    return filteredStages.sort(
      (a: any, b: any) => (a.order || 0) - (b.order || 0)
    );
  }

  private getStageTeamIds(stage: any): Array<number | string> {
    if (Array.isArray(stage?.teamIds)) {
      return stage.teamIds
        .map((team: any) => team?.id ?? team)
        .filter((stageTeamId: any) => stageTeamId !== null && stageTeamId !== undefined);
    }

    if (Array.isArray(stage?.teams)) {
      return stage.teams
        .map((team: any) => team?.id ?? team)
        .filter((stageTeamId: any) => stageTeamId !== null && stageTeamId !== undefined);
    }

    return [];
  }

  private stageBelongsToTeam(stage: any, teamId: number | string | null | undefined): boolean {
    if (stage?.isGlobal === true) {
      return true;
    }

    const stageTeamIds = this.getStageTeamIds(stage);

    if (teamId === null || teamId === undefined || teamId === '') {
      return stageTeamIds.length === 0;
    }

    return stageTeamIds.some((stageTeamId) => Number(stageTeamId) === Number(teamId));
  }

  private getStageName(stageId: number | null): string {
    if (stageId === null || stageId === undefined) return 'Nuevo';
    const stage = this.stages.find((s: any) => s.id === stageId);
    return stage?.name || 'Nuevo';
  }

  private formatDateValue(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return '-';
    }
  }

  private getDateForPickerValue(dateString: string | null): Date | null {
    if (!dateString) return null;
    try {
      return new Date(dateString);
    } catch {
      return null;
    }
  }

  // Filtrar usuarios/comerciales por equipo de ventas
  private filterUsersByTeam(teamId: number | null | undefined): void {
    if (!teamId) {
      // Si no hay equipo seleccionado, no mostrar usuarios
      this.filteredUsersForTeam = [];
      return;
    }

    // Buscar el equipo en la lista de equipos
    const salesTeams = this.globalValuesService.salesTeams$.value || [];
    const selectedTeam = salesTeams.find((team: any) => team.id === teamId);

    if (!selectedTeam) {
      // Si no se encuentra el equipo, no mostrar usuarios
      this.filteredUsersForTeam = [];
      return;
    }

    // Obtener los userIds de los miembros del equipo
    const teamMemberUserIds: number[] = [];

    // Verificar si el equipo tiene memberList o members (usar any para evitar errores de tipo)
    const team = selectedTeam as any;
    const members = team.memberList || team.members || [];

    members.forEach((member: any) => {
      // member puede ser un objeto con userId o solo el id directamente
      if (typeof member === 'object' && member !== null) {
        const userId = member.userId || member.id;
        if (userId != null && userId !== undefined) {
          teamMemberUserIds.push(userId);
        }
      } else if (typeof member === 'number') {
        teamMemberUserIds.push(member);
      }
    });

    // Si no hay miembros, no mostrar usuarios (se mostrará un mensaje en el HTML)
    if (teamMemberUserIds.length === 0) {
      this.filteredUsersForTeam = [];
      return;
    }

    // Filtrar los usuarios que pertenecen al equipo
    const allUsers = this.globalValuesService.users$.value || [];
    this.filteredUsersForTeam = allUsers.filter((user: any) =>
      teamMemberUserIds.includes(user.id)
    );
  }

  // Métodos de edición (delegados al padre)
  isFieldEditing(fieldPath: string): boolean {
    return this.currentEditingField === fieldPath;
  }

  onFieldClick(fieldPath: string, event: MouseEvent): void {
    if (!this.editPermissions) return; // Sin permisos: no hacer nada; el enlace redirigirá
    // En modo edición: evitar que el enlace abra la URL y abrir Quill
    if ((event.target as HTMLElement)?.closest?.('a')) {
      event.preventDefault();
    }
    event.stopPropagation();
    this.startEditingField(fieldPath);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.currentEditingField) return;

    const target = event.target as HTMLElement | null;
    const activeElement = document.activeElement as HTMLElement | null;
    const activeInputWillBlur = !!activeElement?.matches?.(
      `input[data-field="${this.currentEditingField}"], textarea[data-field="${this.currentEditingField}"]`,
    );

    if (activeInputWillBlur) return;

    const clickedInsideCurrentField = !!target?.closest?.(
      `[data-field="${this.currentEditingField}"], .ql-editor, .ql-toolbar`,
    );
    const clickedInsideOverlay = !!target?.closest?.(
      '.ant-select-dropdown, .ant-picker-dropdown',
    );

    if (!clickedInsideCurrentField && !clickedInsideOverlay) {
      this.stopEditingField(this.currentEditingField);
      this.cdr.detectChanges();
    }
  }

  @HostListener('document:keydown.enter', ['$event'])
  onDocumentEnter(event: KeyboardEvent): void {
    if (!this.currentEditingField) return;

    const activeElement = document.activeElement as HTMLElement | null;
    const activeInput = activeElement?.matches?.(
      `input[data-field="${this.currentEditingField}"], textarea[data-field="${this.currentEditingField}"]`,
    );

    if (activeInput && activeElement) {
      event.preventDefault();
      activeElement.blur();
    }
  }

  onFieldBlur(fieldPath: string, event: FocusEvent | string): void {
    if (this.currentEditingField === fieldPath) {
      // Si event es un string, viene de Quill (onQuillBlur) - ya viene normalizado
      // Si es FocusEvent, viene de un input/textarea normal
      let newValue: string;
      if (typeof event === 'string') {
        // Ya viene normalizado desde onQuillBlur, usar directamente
        newValue = event;
      } else {
        const target = event.target as HTMLInputElement;
        newValue = target?.value || '';
      }

      // Para campos de texto enriquecido (description), asegurar que sea cadena vacía, no null
      if (
        fieldPath === 'description' &&
        (newValue === null || newValue === undefined)
      ) {
        newValue = '';
      }

      // Guardar el valor anterior como backup antes de emitir
      const previousValue =
        fieldPath === 'name'
          ? this.opportunity?.oportunidad ||
            this.opportunity?.originalOpportunityName ||
            this.opportunity?.name
          : this.backupFieldValues[fieldPath] !== undefined
          ? this.backupFieldValues[fieldPath]
          : this.opportunity
          ? this.opportunity[fieldPath]
          : undefined;

      // Actualizar el valor en el objeto opportunity (solo localmente, no guardar aún)
      if (this.opportunity) {
        if (fieldPath === 'name') {
          // Para el nombre de la oportunidad, actualizar oportunidad, originalOpportunityName y name
          this.opportunity.oportunidad = newValue;
          this.opportunity.originalOpportunityName = newValue;
          this.opportunity.name = newValue;
          console.log('📝 Nombre actualizado en hijo (opportunity-content):', {
            newValue,
            oportunidad: this.opportunity.oportunidad,
            originalOpportunityName: this.opportunity.originalOpportunityName,
            name: this.opportunity.name,
          });
        } else {
          this.opportunity[fieldPath] = newValue;
        }
      }

      // Limpiar error del campo al editar nuevamente
      if (this.fieldErrors[fieldPath]) {
        delete this.fieldErrors[fieldPath];
      }

      // Emitir el evento de actualización con previousValue
      // Asegurar que description siempre sea cadena vacía, no null
      const emitValue =
        fieldPath === 'description' &&
        (newValue === null || newValue === undefined)
          ? ''
          : newValue;

      // Para description, verificar que no sea un cambio duplicado o que no esté vacío cuando debería tener contenido
      if (fieldPath === 'description') {
        // Si el último contenido emitido es igual al nuevo, no emitir (evitar duplicados)
        if (this.lastEmittedContent === emitValue) {
          console.log('🚫 Ignorando emisión duplicada de description');
          this.stopEditingField(fieldPath);
          this.updateComputedProperties();
          return;
        }

        // CRÍTICO: Si el nuevo valor está vacío pero el previousValue tenía contenido con imágenes, no emitir
        // (esto evita que se emita un cambio vacío después de insertar una imagen)
        const previousHadImage =
          previousValue &&
          typeof previousValue === 'string' &&
          previousValue.includes('<img');
        const currentIsEmpty =
          !emitValue ||
          emitValue === '' ||
          emitValue.trim() === '' ||
          emitValue.trim() === '<p><br></p>' ||
          emitValue.trim() === '<p></p>';

        if (currentIsEmpty && previousHadImage) {
          console.log(
            '🚫 Ignorando emisión de description vacío cuando había imagen antes',
            {
              previousValueLength: previousValue?.length || 0,
              emitValueLength: emitValue?.length || 0,
            }
          );
          this.stopEditingField(fieldPath);
          this.updateComputedProperties();
          return;
        }

        // Si el nuevo valor tiene imagen pero el previousValue también tenía imagen y son iguales, no emitir
        const currentHasImage =
          emitValue &&
          typeof emitValue === 'string' &&
          emitValue.includes('<img');
        if (
          currentHasImage &&
          previousHadImage &&
          emitValue === previousValue
        ) {
          console.log('🚫 Ignorando emisión de description con misma imagen');
          this.stopEditingField(fieldPath);
          this.updateComputedProperties();
          return;
        }

        // Guardar el contenido emitido
        this.lastEmittedContent = emitValue;
      }

      this.fieldUpdate.emit({
        path: fieldPath,
        value: emitValue,
        previousValue: previousValue,
      });
      console.log('📤 Emitiendo fieldUpdate:', {
        path: fieldPath,
        value: emitValue,
        previousValue: previousValue,
        valueLength: emitValue?.length || 0,
      });

      // Solo detener la edición, no guardar aún
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  onQuillContentChanged(fieldPath: string, content: string): void {
    // Si se está insertando una imagen, guardar el contenido pendiente pero NO actualizar ni emitir
    if (this.isInsertingImage) {
      console.log(
        '🚫 Ignorando ngModelChange durante inserción de imagen, guardando contenido pendiente'
      );
      // Guardar el contenido pendiente si tiene imagen
      if (content && content.includes('<img')) {
        this.pendingImageContent = content;
      }
      return;
    }

    if (this.currentEditingField === fieldPath && this.opportunity) {
      // Verificar si el contenido tiene imágenes - si las tiene, no normalizar
      const hasImages = content && content.includes('<img');

      if (hasImages) {
        // Si tiene imágenes, guardar el contenido tal cual sin normalizar
        this.opportunity[fieldPath] = content;
      } else {
        // Solo actualizar el valor local mientras se edita, NO emitir cambios
        // La normalización y emisión se hará solo en onQuillBlur
        // Guardar el contenido tal cual viene de Quill para normalizarlo después en blur
        this.opportunity[fieldPath] = content || '';
      }
    }
  }

  onQuillBlur(fieldPath: string): void {
    // Si se está insertando una imagen, ignorar el blur para evitar emisiones prematuras
    if (this.isInsertingImage) {
      console.log('🚫 Ignorando onBlur durante inserción de imagen');
      return;
    }

    // Si se está agregando un link, ignorar el blur para evitar que se cierre el modo edición
    if (this.isAddingLink) {
      console.log('🚫 Ignorando onBlur durante proceso de agregar link');
      return;
    }

    // Obtener el contenido actual del opportunity directamente del editor si está disponible
    let content = '';

    // Intentar obtener el contenido directamente del editor Quill
    if (this.quillEditor && this.quillEditor.quillEditor) {
      content = this.quillEditor.quillEditor.root.innerHTML || '';
    } else {
      // Fallback: usar el valor del opportunity
      content = this.opportunity?.[fieldPath] || '';
    }

    // CRÍTICO: Si el contenido tiene imágenes, NUNCA normalizar a vacío
    const hasImages = content.includes('<img');

    // Si tiene imágenes, SIEMPRE mantener el contenido completo sin normalizar
    if (hasImages) {
      // Si tiene imágenes, mantener el contenido tal cual, sin normalizar
      // Actualizar el valor en el objeto opportunity
      if (this.opportunity) {
        this.opportunity[fieldPath] = content;
      }
      console.log(
        '📝 onQuillBlur - contenido con imagen, longitud:',
        content.length
      );
      // Emitir el cambio con el contenido completo que incluye la imagen
      this.onFieldBlur(fieldPath, content);
      return; // Salir temprano para evitar normalización
    }

    // Si NO tiene imágenes, proceder con normalización normal
    const hasContent =
      content.replace(/<p><br><\/p>|<p><\/p>|<p><br\/><\/p>/g, '').trim()
        .length > 0;

    // Solo normalizar a vacío si realmente está vacío (sin imágenes ni contenido)
    if (!hasContent) {
      const trimmed = content.trim();
      if (
        trimmed === '' ||
        trimmed === '<p><br></p>' ||
        trimmed === '<p></p>' ||
        trimmed === '<p><br/></p>'
      ) {
        content = ''; // Usar cadena vacía, NO null
      }
    }

    // Actualizar el valor normalizado en el objeto opportunity
    if (this.opportunity) {
      this.opportunity[fieldPath] = content;
    }

    console.log('📝 onQuillBlur - contenido final:', {
      hasImages,
      hasContent,
      contentLength: content.length,
      contentPreview: content.substring(0, 100),
    });

    // Emitir el cambio solo una vez con el valor normalizado
    this.onFieldBlur(fieldPath, content);
  }

  quillImageHandler(): void {
    // IMPORTANTE: Guardar el contenido ANTES de marcar la bandera para tener referencia
    const contentBeforeInsert = this.opportunity?.['description'] || '';
    console.log(
      '🖼️ Iniciando inserción de imagen, contenido antes:',
      contentBeforeInsert.length
    );

    // Marcar que se está insertando una imagen para evitar emisiones prematuras
    // IMPORTANTE: Hacer esto ANTES de crear el input para evitar que onBlur se dispare
    this.isInsertingImage = true;

    // Guardar el contenido anterior como referencia para evitar emisiones vacías
    this.lastEmittedContent = contentBeforeInsert;

    // Crear input de archivo temporal
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const imageUrl = e.target.result;
          // Insertar imagen en Quill usando el editor
          if (this.quillEditor && this.quillEditor.quillEditor) {
            const quill = this.quillEditor.quillEditor;

            // Deshabilitar temporalmente el ngModelChange usando una bandera más robusta
            // Guardar el contenido antes de insertar para comparar después
            const contentBefore = quill.root.innerHTML;

            const range = quill.getSelection(true);
            if (range) {
              quill.insertEmbed(range.index, 'image', imageUrl);
              quill.setSelection(range.index + 1);
            } else {
              const length = quill.getLength();
              quill.insertEmbed(length - 1, 'image', imageUrl);
              quill.setSelection(length);
            }

            // Esperar a que Quill procese completamente la inserción
            // Usar múltiples delays para asegurar que Quill haya procesado todo
            setTimeout(() => {
              // Obtener el contenido final después de que Quill lo procese
              const finalContent = quill.root.innerHTML || '';

              // Verificar que el contenido realmente tiene la imagen y es diferente al anterior
              const hasImage = finalContent.includes('<img');
              const contentChanged = finalContent !== contentBefore;

              console.log('🖼️ Después de insertar imagen:', {
                hasImage,
                contentChanged,
                contentLength: finalContent.length,
                contentBeforeLength: contentBefore.length,
              });

              if (hasImage && contentChanged) {
                // Actualizar el valor con el contenido final que incluye la imagen
                if (this.opportunity) {
                  this.opportunity['description'] = finalContent;
                }

                // Esperar un momento más para asegurar que todos los eventos de Quill hayan terminado
                setTimeout(() => {
                  // Verificar una vez más que el contenido tiene la imagen
                  const verifyContent = quill.root.innerHTML || '';

                  if (verifyContent.includes('<img')) {
                    // Actualizar nuevamente por si acaso
                    if (this.opportunity) {
                      this.opportunity['description'] = verifyContent;
                    }

                    // Restaurar el foco en el editor
                    quill.focus();

                    // Esperar un momento más antes de permitir emisiones y emitir
                    setTimeout(() => {
                      // Obtener el contenido una última vez para asegurar que es correcto
                      const lastContent = quill.root.innerHTML || '';

                      if (lastContent.includes('<img')) {
                        // Actualizar el valor una vez más
                        if (this.opportunity) {
                          this.opportunity['description'] = lastContent;
                        }

                        // Limpiar contenido pendiente si existe
                        this.pendingImageContent = null;

                        // IMPORTANTE: Actualizar lastEmittedContent ANTES de permitir emisiones
                        // para evitar que se emita un cambio vacío después
                        this.lastEmittedContent = lastContent;

                        // Permitir emisiones nuevamente DESPUÉS de actualizar el valor y lastEmittedContent
                        this.isInsertingImage = false;

                        console.log(
                          '✅ Emitiendo cambio con imagen, longitud:',
                          lastContent.length
                        );
                        // Emitir el cambio con el contenido que incluye la imagen
                        this.onFieldBlur('description', lastContent);
                      } else if (
                        this.pendingImageContent &&
                        this.pendingImageContent.includes('<img')
                      ) {
                        // Si el contenido del editor no tiene la imagen pero tenemos contenido pendiente, usar ese
                        if (this.opportunity) {
                          this.opportunity['description'] =
                            this.pendingImageContent;
                        }
                        // IMPORTANTE: Actualizar lastEmittedContent ANTES de permitir emisiones
                        this.lastEmittedContent = this.pendingImageContent;
                        this.isInsertingImage = false;
                        this.pendingImageContent = null;
                        console.log(
                          '✅ Emitiendo cambio con imagen desde contenido pendiente, longitud:',
                          this.lastEmittedContent.length
                        );
                        this.onFieldBlur(
                          'description',
                          this.lastEmittedContent
                        );
                      } else {
                        console.warn(
                          '⚠️ El contenido final no tiene imagen, no emitiendo'
                        );
                        this.isInsertingImage = false;
                        this.pendingImageContent = null;
                      }
                    }, 200);
                  } else {
                    console.warn('⚠️ El contenido verificado no tiene imagen');
                    this.isInsertingImage = false;
                    quill.focus();
                  }
                }, 200);
              } else {
                console.warn('⚠️ No se pudo insertar la imagen correctamente');
                this.isInsertingImage = false;
                quill.focus();
              }
            }, 300);
          } else {
            this.isInsertingImage = false;
          }
        };
        reader.onerror = () => {
          this.isInsertingImage = false;
        };
        reader.readAsDataURL(file);
      } else {
        // Si no se seleccionó archivo, permitir emisiones nuevamente
        this.isInsertingImage = false;
      }
      document.body.removeChild(input);
    });

    // Si se cancela la selección de archivo, también restaurar
    input.addEventListener('cancel', () => {
      this.isInsertingImage = false;
    });

    input.click();
  }

  onSelectChange(fieldPath: string, value: any): void {
    if (this.currentEditingField === fieldPath) {
      // Guardar el valor anterior como backup antes de emitir
      const previousValue =
        this.backupFieldValues[fieldPath] !== undefined
          ? this.backupFieldValues[fieldPath]
          : this.opportunity
          ? this.opportunity[fieldPath]
          : undefined;

      // Limpiar error del campo al editar nuevamente
      if (this.fieldErrors[fieldPath]) {
        delete this.fieldErrors[fieldPath];
      }

      // Actualizar el valor en el objeto opportunity (solo localmente)
      if (this.opportunity) {
        // Si es clientCompany, guardar el nombre de la empresa (string) en lugar del ID
        if (fieldPath === 'clientCompany' && value) {
          const selectedCompany = this.companiesList.find(
            (c: Partner) => c.id === value
          );
          if (selectedCompany) {
            this.opportunity[fieldPath] = selectedCompany.name;
            (this.opportunity as any).clientCompanyId = value; // Partner id para guardar

            this.opportunity.partnerId = null;
            this.opportunity.contactPartner = null;
            this.opportunity.email = null;
            this.opportunity.phone = null;
            this.loadContactsByCompany(value);
            this.fieldUpdate.emit({ path: 'partnerId', value: null });
            this.fieldUpdate.emit({ path: 'email', value: null });
            this.fieldUpdate.emit({ path: 'phone', value: null });
            this.cdr.detectChanges();
          } else {
            this.opportunity[fieldPath] = value;
          }
        } else if (fieldPath === 'clientCompany' && !value) {
          this.opportunity[fieldPath] = null;
          (this.opportunity as any).clientCompanyId = null;
          this.opportunity.partnerId = null;
          this.opportunity.contactPartner = null;
          this.opportunity.email = null;
          this.opportunity.phone = null;
          this.contactList = [];
          this.fieldUpdate.emit({ path: 'partnerId', value: null });
          this.fieldUpdate.emit({ path: 'email', value: null });
          this.fieldUpdate.emit({ path: 'phone', value: null });
          this.cdr.detectChanges();
        } else if (fieldPath === 'currencyId') {
          // Actualizar currencyId directamente en opportunity
          this.opportunity.currencyId = value;
          // También actualizar el objeto currency si está disponible
          if (value) {
            const currency = this.currencies.find((c: any) => c.id === value);
            if (currency) {
              this.opportunity.currency = currency;
            }
          } else {
            this.opportunity.currency = null;
          }
          // Emitir el evento de actualización con el valor correcto
          this.fieldUpdate.emit({
            path: 'currencyId',
            value: value,
            previousValue: previousValue,
          });
          // Detener la edición y actualizar propiedades
          this.stopEditingField(fieldPath);
          this.updateComputedProperties();
          return; // Salir temprano para evitar el emit duplicado
        } else {
          this.opportunity[fieldPath] = value;
        }

        // Si es intermediaryCompanyId, actualizar también el objeto anidado para que el nombre se muestre al instante
        if (fieldPath === 'intermediaryCompanyId') {
          const company = value
            ? this.companiesList.find((c: Partner) => c.id === value)
            : null;
          (this.opportunity as any).intermediaryCompany = company
            ? { id: company.id, name: company.name }
            : null;
        }

        // Mantener sincronizados los objetos usados por los avatares para que cambien al instante
        if (fieldPath === 'partnerId') {
          // Buscar el contacto en contactList primero, si no está ahí buscar en partnerlist
          const partner =
            this.contactList.find((p: any) => p.id === value) ||
            this.globalValuesService.partnerlist.find(
              (p: any) => p.id === value
            );

          if (partner) {
            this.opportunity.contactPartner = partner;

            // Actualizar email y teléfono del contacto
            const partnerEmail = partner.email || null;
            const partnerPhone = partner.phone || null;

            this.opportunity.email = partnerEmail;
            this.opportunity.phone = partnerPhone;

            // Emitir eventos de actualización para email y phone
            this.fieldUpdate.emit({ path: 'email', value: partnerEmail });
            this.fieldUpdate.emit({ path: 'phone', value: partnerPhone });

            // Si el contacto tiene parentId, actualizar Empresa Cliente (Partner id)
            if (partner.parentId) {
              const company = this.companiesList.find(
                (c: Partner) => c.id === partner.parentId
              );
              if (company) {
                this.opportunity.clientCompany = company.name;
                (this.opportunity as any).clientCompanyId = company.id;
                this.fieldUpdate.emit({ path: 'clientCompany', value: company.name });
              }
            }
          }
        }

        if (fieldPath === 'teamId') {
          // Guardar backups de campos relacionados ANTES de cambiar el equipo
          if (
            this.backupFieldValues['userId'] === undefined &&
            this.opportunity?.userId
          ) {
            this.backupFieldValues['userId'] = this.opportunity.userId;
          }
          if (
            this.backupFieldValues['stageId'] === undefined &&
            this.opportunity?.stageId
          ) {
            this.backupFieldValues['stageId'] = this.opportunity.stageId;
          }

          // Filtrar los comerciales disponibles para el nuevo equipo ANTES de actualizar
          this.filterUsersByTeam(value);

          // Verificar si el comercial actual pertenece al nuevo equipo
          if (value && this.opportunity?.userId) {
            const belongsToNewTeam = this.filteredUsersForTeam.some(
              (u: any) => u.id === this.opportunity.userId
            );
            if (!belongsToNewTeam) {
              // El comercial actual no pertenece al nuevo equipo, limpiarlo
              const previousUserId = this.opportunity.userId;
              this.opportunity.userId = null;
              this.opportunity.comercialUser = null;
              // Emitir el cambio de userId
              this.fieldUpdate.emit({
                path: 'userId',
                value: null,
                previousValue: previousUserId,
              });
            }
          } else if (!value) {
            // Si se limpia el equipo, limpiar también el comercial
            const previousUserId = this.opportunity?.userId;
            this.opportunity.userId = null;
            this.opportunity.comercialUser = null;
            if (previousUserId) {
              this.fieldUpdate.emit({
                path: 'userId',
                value: null,
                previousValue: previousUserId,
              });
            }
          }

          // Verificar si el stageId actual pertenece al nuevo equipo
          if (value && this.opportunity?.stageId) {
            const stagesForNewTeam = this.getStagesForTeam(value);
            const stageBelongsToNewTeam = stagesForNewTeam.some(
              (s: any) => s.id === this.opportunity.stageId
            );
            if (!stageBelongsToNewTeam) {
              // El stage actual no pertenece al nuevo equipo, asignar el primer stage del nuevo equipo
              const previousStageId = this.opportunity.stageId;
              if (stagesForNewTeam.length > 0) {
                // Asignar el primer stage del nuevo equipo (ordenado por sequence)
                const firstStage = stagesForNewTeam[0];
                this.opportunity.stageId = firstStage.id;
                // Actualizar también el objeto stage si está disponible
                if (firstStage) {
                  this.opportunity.stage = firstStage;
                }
                // Emitir el cambio de stageId
                this.fieldUpdate.emit({
                  path: 'stageId',
                  value: firstStage.id,
                  previousValue: previousStageId,
                });
              } else {
                // Si no hay stages para el nuevo equipo, mantener el stage actual pero emitir el cambio
                // (el backend debería manejar esto)
                this.fieldUpdate.emit({
                  path: 'stageId',
                  value: this.opportunity.stageId,
                  previousValue: previousStageId,
                });
              }
            }
          } else if (!value) {
            // Si se limpia el equipo, mantener el stage actual (no limpiarlo porque es obligatorio)
            // Solo emitir el cambio de teamId
          }
        }

        if (fieldPath === 'userId') {
          const users = this.globalValuesService.users$.value || [];
          const user = users.find((u: any) => u.id === value);
          // En la UI se usa opportunity.comercialUser.avatar/name/surName (shape de partner)
          if (user?.partner) {
            this.opportunity.comercialUser = user.partner;
          }
        }
      }

      // Emitir el evento de actualización con el valor correcto (nombre para clientCompany)
      const emitValue =
        fieldPath === 'clientCompany' && value
          ? this.companiesList.find((c: Partner) => c.id === value)?.name ||
            value
          : value;
      this.fieldUpdate.emit({
        path: fieldPath,
        value: emitValue,
        previousValue: previousValue,
      });

      // Solo detener la edición
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  // Método para cargar contactos filtrados por empresa cliente
  loadContactsByCompany(companyId: number): void {
    if (!companyId) {
      this.contactList = [];
      return;
    }

    const payload = {
      filterGroups: [
        [
          { property: 'isCompany', value: 'false', operator: 'Equals' },
          {
            property: 'parentId',
            value: String(companyId),
            operator: 'Equals',
          },
        ],
      ],
      startRow: 0,
      endRow: 99999999,
      sortDirection: 'asc',
      sortProperty: 'name',
    };

    this.contactLoading = true;
    this.partnerService
      .postAdvanced(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.contactLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (rows: Partner[]) => {
          this.contactList = rows || [];
          this.cdr.detectChanges();
        },
        error: () => {
          this.contactList = [];
          this.cdr.detectChanges();
        },
      });
  }

  /** Texto a mostrar en Empresa Cliente: por clientCompanyId (Partner id) desde companiesList, no companyId */
  getOpportunityClientCompanyDisplay(): string {
    const oppAny = this.opportunity as any;
    const partnerId = oppAny?.clientCompanyId;
    if (partnerId != null) {
      const company = this.companiesList.find((c: Partner) => c.id === partnerId);
      if (company?.name) return String(company.name).trim();
    }
    const c = this.opportunity?.clientCompany;
    if (c && String(c).trim()) return String(c).trim();
    if (this.opportunity?.partnerId) {
      const contactPartner =
        this.opportunity.contactPartner ||
        this.globalValuesService.partnerlist?.find(
          (p: Partner) => p.id === this.opportunity.partnerId
        );
      if (contactPartner?.parentId) {
        const company = this.companiesList.find(
          (co: Partner) => co.id === contactPartner.parentId
        );
        if (company?.name) return String(company.name).trim();
      }
    }
    // API puede traer company (nested): mostrar solo si está en companiesList (Partner)
    const nestedName = oppAny?.company?.name;
    if (nestedName) {
      const byName = this.companiesList.find(
        (c: Partner) => c.name === nestedName
      );
      if (byName) return String(byName.name).trim();
    }
    return '-';
  }

  /** ID de la empresa cliente = Partner id de companiesList (no companyId). Para edición y detalle. */
  getClientCompanyId(
    clientCompanyName?: string | null
  ): number | null {
    const oppAny = this.opportunity as any;
    if (oppAny?.clientCompanyId != null) return oppAny.clientCompanyId;
    if (this.opportunity?.partnerId) {
      const contactPartner =
        this.opportunity.contactPartner ||
        this.globalValuesService.partnerlist.find(
          (p: Partner) => p.id === this.opportunity.partnerId
        );
      if (contactPartner?.parentId) {
        const company = this.companiesList.find(
          (c: Partner) => c.id === contactPartner.parentId
        );
        if (company) return company.id;
      }
    }
    if (clientCompanyName) {
      const company = this.companiesList.find(
        (c: Partner) => c.name === clientCompanyName
      );
      if (company) return company.id;
    }
    // API puede traer company (nested): usar solo si está en companiesList
    if (oppAny?.company?.name) {
      const company = this.companiesList.find(
        (c: Partner) => c.name === oppAny.company.name
      );
      if (company) return company.id;
    }
    return null;
  }

  // Función para deshabilitar fechas anteriores a hoy (pero que se vean normales)
  disabledPastDate = (current: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentDate = new Date(current);
    currentDate.setHours(0, 0, 0, 0);
    // Retorna true si la fecha es anterior a hoy (debe estar deshabilitada)
    return currentDate < today;
  };

  onDateChange(fieldPath: string, value: Date | null): void {
    if (this.currentEditingField === fieldPath) {
      const dateValue = value ? value.toISOString() : null;

      // Actualizar el valor en el objeto opportunity (solo localmente)
      if (this.opportunity) {
        this.opportunity[fieldPath] = dateValue;
      }

      // Solo detener la edición
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  onArrayChange(fieldPath: string, value: any[]): void {
    if (this.currentEditingField === fieldPath) {
      this.tempFieldValues[fieldPath] = value;
    }
  }

  onArraySelectClose(fieldPath: string, isOpen: boolean): void {
    if (!isOpen && this.currentEditingField === fieldPath) {
      const value = this.tempFieldValues[fieldPath] || this.getTagIdsValue();

      // Para tags, obtener el valor anterior antes de actualizar
      let previousValue = undefined;
      if (
        fieldPath === 'tags' &&
        this.backupFieldValues[fieldPath] !== undefined
      ) {
        previousValue = this.backupFieldValues[fieldPath];
      }

      // Actualizar el valor en el objeto opportunity (solo localmente)
      if (this.opportunity) {
        this.opportunity[fieldPath] = value;
      }

      // Solo detener la edición, no emitir evento de guardado
      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  onOpportunityTypeChange(value: string): void {
    const typeValue =
      value === 'Proyecto'
        ? 'project'
        : value === 'Servicio Puntual'
        ? 'punctualservice'
        : value;

    // Actualizar el valor en el objeto opportunity (solo localmente)
    if (this.opportunity) {
      this.opportunity.opportunityType = typeValue;
    }

    // Solo detener la edición
    this.stopEditingField('opportunityType');
    this.updateComputedProperties();
  }

  private startEditingField(fieldPath: string): void {
    if (this.currentEditingField && this.currentEditingField !== fieldPath) {
      this.stopEditingField(this.currentEditingField);
    }

    // Guardar el valor actual como backup ANTES de empezar a editar
    const backupValue = this.opportunity?.[fieldPath];
    this.backupFieldValues[fieldPath] = backupValue;

    // Para description, inicializar el último contenido emitido
    if (fieldPath === 'description') {
      this.lastEmittedContent = backupValue || null;
    }

    console.log('💾 Backup guardado para', fieldPath, ':', backupValue);

    // Si se está editando el equipo, guardar también los backups de campos relacionados
    if (fieldPath === 'teamId') {
      if (
        this.backupFieldValues['userId'] === undefined &&
        this.opportunity?.userId
      ) {
        this.backupFieldValues['userId'] = this.opportunity.userId;
        console.log('💾 Backup guardado para userId:', this.opportunity.userId);
      }
      if (
        this.backupFieldValues['stageId'] === undefined &&
        this.opportunity?.stageId
      ) {
        this.backupFieldValues['stageId'] = this.opportunity.stageId;
        console.log(
          '💾 Backup guardado para stageId:',
          this.opportunity.stageId
        );
      }
    }

    this.currentEditingField = fieldPath;

    // Limpiar error del campo al empezar a editarlo
    if (this.fieldErrors[fieldPath]) {
      delete this.fieldErrors[fieldPath];
      this.cdr.detectChanges();
    }

    if (fieldPath === 'tags') {
      this.tempFieldValues[fieldPath] = this.getTagIdsValue();
    }

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  private stopEditingField(fieldPath: string): void {
    if (this.currentEditingField === fieldPath) {
      this.currentEditingField = null;
      delete this.backupFieldValues[fieldPath];
      delete this.tempFieldValues[fieldPath];

      // Si se detiene la edición del equipo, limpiar también los backups de campos relacionados
      // solo si no se está cancelando (porque cancelFieldEdit necesita esos backups)
      // Esto se manejará en cancelFieldEdit
    }
  }

  onEditOverlayOpenChange(fieldPath: string, isOpen: boolean | Event): void {
    const overlayIsOpen = typeof isOpen === 'boolean' ? isOpen : false;
    if (!overlayIsOpen) {
      setTimeout(() => {
        if (this.currentEditingField === fieldPath) {
          this.stopEditingField(fieldPath);
          this.cdr.detectChanges();
        }
      }, 0);
    }
  }

  cancelFieldEdit(fieldPath: string): void {
    if (this.backupFieldValues[fieldPath] !== undefined) {
      if (this.opportunity) {
        const backupValue = this.backupFieldValues[fieldPath];
        const previousValue = this.opportunity[fieldPath];
        this.opportunity[fieldPath] = backupValue;

        // Emitir evento para notificar al padre sobre el cambio restaurado
        this.fieldUpdate.emit({
          path: fieldPath,
          value: backupValue,
          previousValue: previousValue,
        });

        // Si se cancela el cambio de teamId, restaurar también los campos relacionados
        if (fieldPath === 'teamId') {
          // Restaurar el equipo
          this.opportunity.teamId = backupValue;

          // Filtrar comerciales para el equipo restaurado
          this.filterUsersByTeam(backupValue);

          // Restaurar userId si hay backup y pertenece al equipo restaurado
          const userIdBackup = this.backupFieldValues['userId'];
          if (userIdBackup !== undefined && userIdBackup !== null) {
            const belongsToRestoredTeam = this.filteredUsersForTeam.some(
              (u: any) => u.id === userIdBackup
            );
            if (belongsToRestoredTeam) {
              // Restaurar el userId si pertenece al equipo restaurado
              const previousUserId = this.opportunity.userId;
              this.opportunity.userId = userIdBackup;
              const users = this.globalValuesService.users$.value || [];
              const user = users.find((u: any) => u.id === userIdBackup);
              if (user?.partner) {
                this.opportunity.comercialUser = user.partner;
              }
              // Emitir evento para userId restaurado
              this.fieldUpdate.emit({
                path: 'userId',
                value: userIdBackup,
                previousValue: previousUserId,
              });
            } else {
              // Si no pertenece, limpiar el userId
              const previousUserId = this.opportunity.userId;
              this.opportunity.userId = null;
              this.opportunity.comercialUser = null;
              // Emitir evento para userId limpiado
              this.fieldUpdate.emit({
                path: 'userId',
                value: null,
                previousValue: previousUserId,
              });
            }
          }

          // Restaurar stageId si hay backup y pertenece al equipo restaurado
          const stageIdBackup = this.backupFieldValues['stageId'];
          if (stageIdBackup !== undefined && stageIdBackup !== null) {
            const stagesForRestoredTeam = this.getStagesForTeam(backupValue);
            const stageBelongsToRestoredTeam = stagesForRestoredTeam.some(
              (s: any) => s.id === stageIdBackup
            );
            if (stageBelongsToRestoredTeam) {
              // Restaurar el stageId si pertenece al equipo restaurado
              const previousStageId = this.opportunity.stageId;
              this.opportunity.stageId = stageIdBackup;
              // Actualizar también el objeto stage
              const restoredStage = stagesForRestoredTeam.find(
                (s: any) => s.id === stageIdBackup
              );
              if (restoredStage) {
                this.opportunity.stage = restoredStage;
              }
              // Emitir evento para stageId restaurado
              this.fieldUpdate.emit({
                path: 'stageId',
                value: stageIdBackup,
                previousValue: previousStageId,
              });
            } else {
              // Si no pertenece, asignar el primer stage del equipo restaurado para evitar el error del backend
              const previousStageId = this.opportunity.stageId;
              if (stagesForRestoredTeam.length > 0) {
                const firstStage = stagesForRestoredTeam[0];
                this.opportunity.stageId = firstStage.id;
                this.opportunity.stage = firstStage;
                // Emitir evento para stageId asignado
                this.fieldUpdate.emit({
                  path: 'stageId',
                  value: firstStage.id,
                  previousValue: previousStageId,
                });
              } else {
                // Si no hay stages, mantener el backup original (el backend debería manejarlo)
                this.opportunity.stageId = stageIdBackup;
                // Emitir evento para stageId restaurado
                this.fieldUpdate.emit({
                  path: 'stageId',
                  value: stageIdBackup,
                  previousValue: previousStageId,
                });
              }
            }
          } else {
            // Si no hay backup de stageId, asegurar que haya un stage válido para el equipo restaurado
            const stagesForRestoredTeam = this.getStagesForTeam(backupValue);
            if (
              stagesForRestoredTeam.length > 0 &&
              !this.opportunity?.stageId
            ) {
              const previousStageId = this.opportunity.stageId;
              const firstStage = stagesForRestoredTeam[0];
              this.opportunity.stageId = firstStage.id;
              this.opportunity.stage = firstStage;
              // Emitir evento para stageId asignado
              this.fieldUpdate.emit({
                path: 'stageId',
                value: firstStage.id,
                previousValue: previousStageId,
              });
            }
          }
        }
      }
    }
    // Limpiar error al cancelar
    if (this.fieldErrors[fieldPath]) {
      delete this.fieldErrors[fieldPath];
    }

    // Limpiar backups de campos relacionados después de restaurar
    if (fieldPath === 'teamId') {
      delete this.backupFieldValues['userId'];
      delete this.backupFieldValues['stageId'];
    }

    this.stopEditingField(fieldPath);
    this.updateComputedProperties();
  }

  // Verificar si un campo tiene error
  hasFieldError(fieldPath: string): boolean {
    return !!this.fieldErrors[fieldPath];
  }

  // Obtener el mensaje de error de un campo
  getFieldError(fieldPath: string): string | null {
    return this.fieldErrors[fieldPath] || null;
  }

  // Stage methods
  isCurrentStage(stageName: string): boolean {
    return this.currentStageName === stageName;
  }

  private getProbabilityFromStageId(
    stageId: number | null | undefined
  ): number | null {
    if (stageId === null || stageId === undefined) return null;
    const stage = this.stages?.find((s: any) => s.id === stageId);
    if (
      stage &&
      stage.probability !== undefined &&
      stage.probability !== null
    ) {
      return stage.probability;
    }
    return null;
  }

  changeStage(stage: any): void {
    if (!this.editPermissions) return;
    const path = 'stageId';
    const value = stage.id;
    const backupValue = this.opportunity?.stageId;
    if (backupValue === value || backupValue == value) return;

    if (!this.backupFieldValues[path]) {
      this.backupFieldValues[path] = backupValue;
    }

    // Actualizar el valor en el objeto opportunity (solo localmente)
    if (this.opportunity) {
      this.opportunity.stageId = value;

      // Si la etapa tiene probabilidad definida (o la buscamos por id), actualizarla automáticamente
      const stageProbability =
        stage?.probability !== null && stage?.probability !== undefined
          ? stage.probability
          : this.getProbabilityFromStageId(value);
      if (stageProbability !== null && stageProbability !== undefined) {
        this.opportunity.probability = stageProbability;
        this.probabilityForInput = stageProbability;
      }
    }

    // Solo actualizar propiedades, no emitir evento de guardado
    this.updateComputedProperties();
  }

  getStageIcon(index: number): string {
    const icons = [
      'circle',
      'circle',
      'circle',
      'pause',
      'check_circle',
      'cancel',
    ];
    return icons[index] || 'circle';
  }

  getStageColor(index: number): string {
    const colors = [
      '#0072F5',
      '#8E4EC6',
      '#F5A623',
      '#666666',
      '#6CDA75',
      '#E53E3E',
    ];
    return colors[index] || '#666666';
  }

  getStageHoverClass(index: number): string {
    const classes = ['blue-hover', 'purple-hover', 'orange-hover', '', '', ''];
    return classes[index] || '';
  }

  removeTag(tagId: number, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.opportunity?.tags) return;
    const currentTags = Array.isArray(this.opportunity.tags)
      ? [...this.opportunity.tags]
      : [];
    const filteredTags = currentTags.filter((tag: any) => {
      if (typeof tag === 'object') {
        return tag.id !== tagId;
      }
      return tag !== tagId;
    });

    // Convertir a array de IDs
    const tagIds = filteredTags.map((tag: any) =>
      typeof tag === 'object' ? tag.id : tag
    );

    // Actualizar el valor en el objeto opportunity (solo localmente)
    if (this.opportunity) {
      this.opportunity.tags = tagIds;
    }

    // Solo actualizar propiedades, no emitir evento de guardado
    this.updateComputedProperties();
  }

  getProjectName(projectId: number): string {
    const project = this.projects.find((p) => p.id === projectId);
    return project?.name || `Proyecto ${projectId}`;
  }

  getProjectDisplayName(project: any): string {
    // Si es un objeto con propiedades, usar directamente el nombre
    if (project && typeof project === 'object' && project.name) {
      return project.name;
    }
    // Si es un número (ID), buscar en la lista de proyectos
    if (typeof project === 'number') {
      return this.getProjectName(project);
    }
    // Si tiene id o projectId, buscar en la lista
    const id =
      project && typeof project === 'object'
        ? project.id ?? project.projectId
        : null;
    if (id != null) {
      return this.getProjectName(typeof id === 'number' ? id : Number(id));
    }
    // Fallback
    return `Proyecto ${project}`;
  }

  getProjectId(project: any): number | null {
    if (!project) return null;
    if (typeof project === 'number') return project;
    if (typeof project === 'object') {
      const id = project.id ?? project.projectId ?? project.project?.id;
      if (id != null) {
        const n = typeof id === 'number' ? id : Number(id);
        return isNaN(n) ? null : n;
      }
    }
    return null;
  }

  navigateToProject(project: any): void {
    const id = this.getProjectId(project);
    if (id != null) {
      this.router.navigate(['/projects', 'detail', id]).then();
    }
  }

  /** Navega al detalle del proyecto; emite al padre (drawer) para que cierre y navegue correctamente */
  onProjectLinkClick(event: Event, project: any): void {
    event.preventDefault();
    event.stopPropagation();
    const id = this.getProjectId(project);
    if (id == null) return;
    this.navigateToProjectRequest.emit(id);
  }

  editProject(project: any): void {
    this.navigateToProject(project);
  }

  onDeleteProject(project: any, event: Event): void {
    event.stopPropagation();
    const id = this.getProjectId(project);
    if (id == null) return;
    this.pendingDeleteProjectId = id;
    const name = this.getProjectDisplayName(project);
    this.deleteProjectConfirmationModal.config = {
      title: 'Desvincular proyecto',
      message: `¿Estás seguro de que deseas desvincular el proyecto "${name}" de esta oportunidad?`,
      confirmText: 'Desvincular',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.deleteProjectConfirmationModal.open();
  }

  onDeleteProjectConfirmed(event: any): void {
    if (this.pendingDeleteProjectId == null) return;
    const projectId = this.pendingDeleteProjectId;
    this.pendingDeleteProjectId = null;
    const opportunityId = this.opportunity?.id ?? this.opportunity?.leadId;
    if (opportunityId == null) {
      this.modalService.error({
        nzTitle: 'Error',
        nzContent: 'No se encontró la oportunidad asociada.',
        nzCentered: true,
      });
      return;
    }
    this.opportunityService
      .unlinkProject(opportunityId, projectId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {})
      )
      .subscribe({
        next: (response: any) => {
          // El backend ahora calcula métricas automáticamente al desvincular proyectos.
          // Si la respuesta incluye la oportunidad actualizada con projects y metrics, usarla.
          // Si no, filtrar localmente y el drawer padre recargará si es necesario.
          if (response?.data) {
            const updatedOpportunity = response.data;
            if (
              updatedOpportunity?.projects &&
              Array.isArray(updatedOpportunity.projects)
            ) {
              // Usar la respuesta del backend que incluye métricas actualizadas
              this.opportunity = {
                ...this.opportunity,
                projects: updatedOpportunity.projects,
              };
              this.updateEvent.emit(this.opportunity);
              this.projectDeleted.emit(projectId);
              this.cdr.detectChanges();
              return;
            }
          }

          // Fallback: filtrar localmente si el backend no devuelve la oportunidad actualizada
          if (
            this.opportunity?.projects &&
            Array.isArray(this.opportunity.projects)
          ) {
            this.opportunity.projects = this.opportunity.projects.filter(
              (p: any) => this.getProjectId(p) !== projectId
            );
            this.updateEvent.emit(this.opportunity);
            this.projectDeleted.emit(projectId);
            this.cdr.detectChanges();
          }
        },
        error: (err: any) => {
          console.error('Error desvinculando proyecto:', err);
          this.modalService.error({
            nzTitle: 'Error',
            nzContent:
              err?.error?.message ||
              err?.message ||
              'No se pudo desvincular el proyecto.',
            nzCentered: true,
          });
        },
      });
  }

  getProjectTasksCount(project: any): number {
    const fromMetrics = project?.metrics?.taskCount;
    if (typeof fromMetrics === 'number') return fromMetrics;
    if (!project?.services || !Array.isArray(project.services)) return 0;
    return project.services.length;
  }
  getProjectSubtasksCount(project: any): number {
    const fromMetrics = project?.metrics?.subtaskCount;
    if (typeof fromMetrics === 'number') return fromMetrics;
    if (!project?.services || !Array.isArray(project.services)) return 0;
    return project.services.reduce(
      (total: number, service: any) =>
        total + (service?.serviceOrders?.length ?? 0),
      0
    );
  }

  /** Porcentaje de tareas completadas (0-100) para el progress circular. */
  getProjectTasksPercent(project: any): number {
    const total = this.getProjectTasksCount(project);
    if (total === 0) return 0;
    const completed = project?.metrics?.completedTaskCount;
    if (typeof completed !== 'number') return 0;
    return Math.min(100, Math.round((completed / total) * 100));
  }

  /** Porcentaje de subtareas completadas (0-100) para el progress circular. */
  getProjectSubtasksPercent(project: any): number {
    const total = this.getProjectSubtasksCount(project);
    if (total === 0) return 0;
    const completed = project?.metrics?.completedSubtaskCount;
    if (typeof completed !== 'number') return 0;
    return Math.min(100, Math.round((completed / total) * 100));
  }

  /** Formato "6 / 8 Tareas" desde metrics (completedTaskCount / taskCount) o fallback. */
  getProjectTasksSummary(project: any): string {
    const total = this.getProjectTasksCount(project);
    const completed = project?.metrics?.completedTaskCount;
    if (typeof completed === 'number') return `${completed} / ${total} Tareas`;
    return `${total} Tareas`;
  }

  /** Formato "20 / 28 Tareas" desde metrics (completedSubtaskCount / subtaskCount) o fallback. */
  getProjectSubtasksSummary(project: any): string {
    const total = this.getProjectSubtasksCount(project);
    const completed = project?.metrics?.completedSubtaskCount;
    if (typeof completed === 'number') return `${completed} / ${total} Tareas`;
    return `${total} Tareas`;
  }

  /** Resumen: "apox. 80% completado" si hay completionPercent, sino descripción. */
  getProjectSummaryText(project: any): string {
    const pct = project?.metrics?.completionPercent;
    if (typeof pct === 'number') return `apox. ${pct}% completado`;
    const summary = project?.summary ?? project?.description ?? project?.notes;
    if (summary && typeof summary === 'string' && summary.trim())
      return summary.trim();
    return '—';
  }

  getProjectLastUpdate(project: any): string {
    const date =
      project?.metrics?.lastUpdatedOn ??
      project?.updatedAt ??
      project?.lastUpdate ??
      project?.modifiedAt ??
      project?.updatedDate ??
      project?.lastModified;
    if (!date) return '—';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
    } catch {
      return '—';
    }
  }

  /** Última actualización en formato relativo: "Hace 2 días". */
  getProjectLastUpdateRelative(project: any): string {
    const date =
      project?.metrics?.lastUpdatedOn ??
      project?.updatedAt ??
      project?.lastUpdate ??
      project?.modifiedAt ??
      project?.updatedDate ??
      project?.lastModified;
    if (!date) return '—';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(d.getTime())) return '—';
      const text = formatDistanceToNow(d, { addSuffix: true, locale: es });
      return text.charAt(0).toUpperCase() + text.slice(1);
    } catch {
      return '—';
    }
  }

  getProjectCompletionPercent(project: any): number | null {
    const pct = project?.metrics?.completionPercent;
    if (typeof pct !== 'number') return null;
    return pct;
  }

  /** Etapa de proyecto (status): Planificación, En ejecución, etc. Icon + badge. */
  getProjectStageDisplay(
    project: any
  ): { name: string; icon: string; class: string } | null {
    return project?.status
      ? this.getProjectStatusDisplay(project.status)
      : null;
  }

  /** Estado del proyecto (ProjectState): Critical=0, Bad=1, Regular=2, Good=3, VeryGood=4. Icon + badge con color. */
  getProjectStateDisplay(
    project: any
  ): { name: string; icon: string; color: string } | null {
    const raw = project?.metrics?.projectState;
    const colorFromApi = project?.metrics?.projectStateColor;
    const resolved = this.resolveProjectState(raw);
    if (!resolved) return null;
    return {
      name: resolved.name,
      icon: resolved.icon,
      color:
        colorFromApi && typeof colorFromApi === 'string'
          ? colorFromApi
          : resolved.color,
    };
  }

  private resolveProjectState(
    state: number | string | null | undefined
  ): { name: string; icon: string; color: string } | null {
    const byNumber: Record<
      number,
      { name: string; icon: string; color: string }
    > = {
      0: { name: 'Crítico', icon: 'warning', color: '#DC2626' },
      1: { name: 'Mal', icon: 'warning', color: '#F97316' },
      2: { name: 'Regular', icon: 'info', color: '#EAB308' },
      3: { name: 'Bien', icon: 'check_circle', color: '#16A34A' },
      4: { name: 'Muy bien', icon: 'check_circle', color: '#2563EB' },
    };
    const byName: Record<
      string,
      { name: string; icon: string; color: string }
    > = {
      Critical: byNumber[0],
      Bad: byNumber[1],
      Regular: byNumber[2],
      Good: byNumber[3],
      VeryGood: byNumber[4],
    };
    if (typeof state === 'number' && state in byNumber) return byNumber[state];
    if (typeof state === 'string' && state in byName) return byName[state];
    return null;
  }

  private getProjectStatusDisplay(
    status: string
  ): { name: string; icon: string; class: string } | null {
    const map: Record<string, { name: string; icon: string; class: string }> = {
      NonValidated: { name: 'No validado', icon: 'close', class: 'subtle-red' },
      Validated: { name: 'Validado', icon: 'verified', class: 'subtle-purple' },
      Planning: {
        name: 'Planificación',
        icon: 'event_upcoming',
        class: 'subtle-pink',
      },
      InExecution: {
        name: 'En ejecución',
        icon: 'motion_play',
        class: 'subtle-blue',
      },
      PaymentStatus: {
        name: 'Estado de pago',
        icon: 'price_change',
        class: 'subtle-teal',
      },
      Finalized: {
        name: 'Finalizado',
        icon: 'check_circle',
        class: 'subtle-green',
      },
    };
    const data = map[status];
    return data
      ? { name: data.name, icon: data.icon, class: data.class }
      : null;
  }
  getProjectSummary(project: any): string {
    const summary = project?.summary ?? project?.description ?? project?.notes;
    if (summary && typeof summary === 'string' && summary.trim())
      return summary.trim();
    return '—';
  }

  getBudgetDisplayName(budget: any): string {
    const opportunityName = (
      this.opportunity?.oportunidad ||
      this.opportunity?.originalOpportunityName ||
      this.opportunity?.name ||
      budget?.lead?.oportunidad ||
      budget?.lead?.originalOpportunityName ||
      budget?.lead?.name ||
      ''
    ).trim();
    const budgetCode = (budget?.budgetCode || '').trim();

    if (opportunityName && opportunityName !== '-' && budgetCode && budgetCode !== '-') {
      return `${opportunityName} # ${budgetCode}`;
    }

    return (budget?.name || opportunityName || budgetCode || '-').trim();
  }

  getBudgetNavigationReference(budget: any): string {
    return resolveSaleOrderNavigationReference(budget);
  }

  onBudgetClick(budget: any, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.budgetClick.emit(budget);
  }

  onDeleteBudget(budget: any, event: MouseEvent): void {
    event.stopPropagation();

    if (!budget?.id || !this.opportunity?.id) return;

    this.pendingDeleteBudgetId = budget.id;
    this.deleteBudgetConfirmationModal.config = {
      title: 'Eliminar presupuesto',
      message: `¿Estás seguro de que deseas eliminar el presupuesto "${
        this.getBudgetDisplayName(budget) || 'Sin nombre'
      }"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.deleteBudgetConfirmationModal.open();
  }

  onDeleteBudgetConfirmed(event: any): void {
    if (this.pendingDeleteBudgetId === null || !this.opportunity?.id) return;

    const leadId = this.opportunity.id || this.opportunity.originalId;
    if (!leadId) return;

    this.opportunityService
      .deleteSaleOrder(leadId, [this.pendingDeleteBudgetId])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.pendingDeleteBudgetId = null;
        })
      )
      .subscribe({
        next: () => {
          // Emitir evento para que el componente padre actualice la lista
          this.budgetDeleted.emit(this.pendingDeleteBudgetId!);
        },
        error: (error: any) => {
          console.error('Error eliminando presupuesto:', error);
          // El modal manejará el error si es necesario
        },
      });
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return '-';
    }
  }

  getPartnerName(partnerId: number | null): string {
    if (!partnerId) return '-';
    const partners = this.globalValuesService.partnerlist || [];
    const partner = partners.find((p: any) => p.id === partnerId);
    if (partner) {
      return `${partner.name || ''} ${partner.surName || ''}`.trim() || '-';
    }
    return '-';
  }

  getPartnerAvatar(partnerId: number | null): string | null {
    if (!partnerId) return null;
    const partners = this.globalValuesService.partnerlist || [];
    const partner = partners.find((p: any) => p.id === partnerId);
    return partner?.avatar || null;
  }

  getBudgetCustomerCompanyName(budget: any): string {
    return this.getPartnerDisplayName(this.resolveBudgetCustomerCompany(budget));
  }

  getBudgetCustomerCompanyAvatar(budget: any): string | null {
    return this.resolveBudgetCustomerCompany(budget)?.avatar || null;
  }

  getBudgetCustomerCompanyInitials(budget: any): string {
    const customerCompany = this.resolveBudgetCustomerCompany(budget);
    if (!customerCompany) return '';
    const name = customerCompany.name || customerCompany.displayName || '';
    const surName = customerCompany.surName || '';
    return `${name.charAt(0)}${surName.charAt(0)}`.trim().toUpperCase();
  }

  private resolveBudgetCustomerCompany(budget: any): any {
    const directCustomerCompany =
      budget?.customerCompany ||
      budget?.lead?.customerCompany ||
      budget?.partner?.parent ||
      budget?.lead?.partner?.parent ||
      budget?.contact?.parent ||
      budget?.lead?.contact?.parent;

    if (directCustomerCompany) {
      return directCustomerCompany;
    }

    const customerCompanyId =
      budget?.customerCompanyId ??
      budget?.lead?.customerCompanyId ??
      budget?.clientCompanyId ??
      budget?.lead?.clientCompanyId ??
      this.opportunity?.clientCompanyId ??
      this.opportunity?.customerCompanyId ??
      null;

    const companyById = this.findPartnerById(customerCompanyId);
    if (companyById) {
      return companyById;
    }

    const contact =
      budget?.contact ||
      (!budget?.partner?.isCompany ? budget?.partner : null) ||
      (!budget?.lead?.partner?.isCompany ? budget?.lead?.partner : null) ||
      this.findPartnerById(budget?.partnerId);

    return this.findPartnerById(contact?.parentId);
  }

  private findPartnerById(partnerId: number | null | undefined): any {
    if (partnerId == null) return null;
    const numericId = Number(partnerId);
    if (!Number.isFinite(numericId)) return null;

    const partnerLists = [
      this.globalValuesService.partnerlist || [],
      this.companiesList || [],
      this.contactList || [],
    ];

    for (const partners of partnerLists) {
      const partner = partners.find((p: any) => Number(p.id) === numericId);
      if (partner) return partner;
    }

    return null;
  }

  private getPartnerDisplayName(partner: any): string {
    if (!partner) return '-';
    return (
      partner.displayName ||
      partner.fullName ||
      `${partner.name || ''} ${partner.surName || ''}`.trim() ||
      '-'
    );
  }

  getBudgetStageName(budget: any): string {
    if (!budget) return '-';

    // Si está cancelado
    if (budget.dateCancelled) {
      return 'Cancelado';
    }

    if (budget.isActive === false) {
      return 'Borrador';
    }

    // Mapeo de estados
    const stageNameMap: { [key: string]: string } = {
      Budget: 'Presupuesto',
      BudgetSent: 'Presupuesto enviado',
      SaleOrder: 'Pedido de venta',
    };

    const stage = budget.stage || 'Budget';
    return stageNameMap[stage] || stage || '-';
  }

  getBudgetStageClass(budget: any): string {
    if (!budget) return 'budget-status-default';

    // Si está cancelado
    if (budget.dateCancelled) {
      return 'budget-status-cancelled';
    }

    if (budget.isActive === false) {
      return 'budget-status-draft';
    }

    const stage = budget.stage || 'Budget';
    const stageClassMap: { [key: string]: string } = {
      Budget: 'budget-status-budget',
      BudgetSent: 'budget-status-sent',
      SaleOrder: 'budget-status-order',
    };

    return stageClassMap[stage] || 'budget-status-default';
  }

  onPartnerCreated(newPartner: Partner): void {
    // Agregar el nuevo partner a la lista de contactos
    if (!this.contactList.find((p) => p.id === newPartner.id)) {
      this.contactList.push(newPartner);
    }
    // Actualizar la oportunidad con el nuevo partner
    if (this.opportunity) {
      this.opportunity.partnerId = newPartner.id;
      this.opportunity.contactPartner = newPartner;
      // Actualizar email y teléfono del contacto
      this.opportunity.email = newPartner.email || null;
      this.opportunity.phone = newPartner.phone || null;
      // Emitir eventos de actualización
      this.fieldUpdate.emit({ path: 'partnerId', value: newPartner.id });
      this.fieldUpdate.emit({ path: 'email', value: newPartner.email || null });
      this.fieldUpdate.emit({ path: 'phone', value: newPartner.phone || null });
    }
    // Actualizar propiedades computadas
    this.updateComputedProperties();
  }
}
